// src/pages/admin/company/TrucksTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../../services/firebase";

export default function TrucksTab({ company }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    rego: "",
    loadType: "",
    active: true,
  });

  useEffect(() => {
    const qy = query(
      collection(db, "trucks"),
      where("companyId", "==", company.id)
    );
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.rego || "").localeCompare(b.rego || ""));
      setRows(list);
    });
    return () => unsub();
  }, [company.id]);

  const view = useMemo(() => {
    const s = filter.trim().toLowerCase();
    return !s
      ? rows
      : rows.filter(
          (r) =>
            (r.rego || "").toLowerCase().includes(s) ||
            (r.loadType || "").toLowerCase().includes(s)
        );
  }, [rows, filter]);

  function openNew() {
    setEditing({});
    setForm({ rego: "", loadType: "", active: true });
  }

  function openEdit(r) {
    setEditing(r);
    setForm({
      rego: r.rego || "",
      loadType: r.loadType || "",
      active: !!r.active,
    });
  }

  function close() {
    setEditing(null);
  }

  async function save() {
    const payload = {
      companyId: company.id,
      companyName: company.name,
      rego: form.rego.trim(),
      loadType: form.loadType.trim(),
      active: !!form.active,
      updatedAt: serverTimestamp(),
    };

    if (!editing?.id) {
      await addDoc(collection(db, "trucks"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, "trucks", editing.id), payload);
    }

    setEditing(null);
  }

  async function remove(id) {
    if (!window.confirm("Delete this truck?")) return;
    await deleteDoc(doc(db, "trucks", id));
  }

  return (
    <div className="space-y-4 text-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          className="w-full sm:w-72 rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          placeholder="Search rego or load type"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button
          className="rounded-xl border border-gray-900 bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-black"
          onClick={openNew}
        >
          New truck
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50/90">
            <tr>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Rego
              </th>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Load type
              </th>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Active
              </th>
              <th className="p-4" />
            </tr>
          </thead>

          <tbody>
            {view.map((r) => (
              <tr
                key={r.id}
                className="border-t border-gray-100 align-top transition hover:bg-gray-50/40"
              >
                <td className="p-4">
                  <div className="font-semibold text-gray-900">{r.rego}</div>
                </td>

                <td className="p-4">
                  <span className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
                    {r.loadType || "-"}
                  </span>
                </td>

                <td className="p-4">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium border ${
                      r.active
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-gray-200 bg-gray-100 text-gray-600"
                    }`}
                  >
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </td>

                <td className="p-4 whitespace-nowrap text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 hover:border-gray-400"
                      onClick={() => openEdit(r)}
                    >
                      Edit
                    </button>
                    <button
                      className="rounded-xl border border-red-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-red-600 shadow-sm transition hover:bg-red-50 hover:border-red-300"
                      onClick={() => remove(r.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {view.length === 0 && (
              <tr>
                <td className="p-6 text-sm text-gray-500" colSpan={4}>
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center">
                    No trucks defined.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={close}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing.id ? "Edit truck" : "New truck"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Manage available trucks for this company.
              </p>
            </div>

            <div className="grid gap-4 p-4">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Rego
                </label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Rego"
                  value={form.rego}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, rego: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Load type
                </label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Load type (e.g. Rearlift)"
                  value={form.loadType}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, loadType: e.target.value }))
                  }
                />
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                <label className="inline-flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={form.active}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, active: e.target.checked }))
                    }
                  />
                  <span className="font-medium">Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 p-4">
              <button
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                onClick={close}
              >
                Cancel
              </button>
              <button
                className="rounded-xl border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-black"
                onClick={save}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}